import {Col, Form, Switch} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useEffect, useMemo, useState} from 'react';

import {isEmptyPhoneNumber} from '../../DrawerForm/commonFunctions';

function hasExistingRecordEmail(email: unknown): boolean {
  const normalized = typeof email === 'string' ? email.trim() : '';

  return normalized.length > 1;
}

function isDependentFieldFilled(value: unknown): boolean {
  if (value == null || value === '') {
    return false;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return false;
    }

    if (trimmed.startsWith('+') || /\d/.test(trimmed)) {
      return !isEmptyPhoneNumber(trimmed);
    }

    return true;
  }

  return true;
}

function hasWatchedFormData(fields: string[], values: unknown): boolean {
  if (values == null) {
    return false;
  }

  if (Array.isArray(values)) {
    return values.some((value) => isDependentFieldFilled(value));
  }

  if (typeof values === 'object') {
    return fields.some((field) =>
      isDependentFieldFilled((values as Record<string, unknown>)[field])
    );
  }

  return isDependentFieldFilled(values);
}

export interface UserCreationToggleProps {
  /** Email from loaded record data (update only). Locks toggle when present. */
  existingEmail?: string;
  label?: string;
  tooltip?: React.ComponentProps<typeof Form.Item>['tooltip'];
  dependentFields: string[];
  children: React.ReactNode;
}

const UserCreationToggle: React.FC<UserCreationToggleProps> = ({
  existingEmail,
  label = 'Create User',
  tooltip,
  dependentFields,
  children
}) => {
  const form = useFormInstance();

  const userCreationLocked = hasExistingRecordEmail(existingEmail);

  const [createUserEnabled, setCreateUserEnabled] = useState(false);
  const [userToggled, setUserToggled] = useState(false);

  const dependentValues = Form.useWatch(dependentFields, form);
  const hasFormData = useMemo(
    () => hasWatchedFormData(dependentFields, dependentValues),
    [dependentFields, dependentValues]
  );

  useEffect(() => {
    if (userCreationLocked) {
      return;
    }

    if (hasFormData) {
      setCreateUserEnabled(true);
    } else if (!userToggled) {
      setCreateUserEnabled(false);
    }
  }, [hasFormData, userCreationLocked, userToggled]);

  const showOn = userCreationLocked || createUserEnabled;

  const handleChange = (checked: boolean): void => {
    if (userCreationLocked && !checked) {
      return;
    }

    if (!checked && dependentFields.length > 0) {
      form.resetFields(dependentFields);
    }
    setUserToggled(true);
    setCreateUserEnabled(checked);
  };

  return (
    <>
      <Col className="gutter-row">
        <Form.Item
          label={label}
          labelCol={{span: 24}}
          tooltip={tooltip}
          initialValue={false}
          className="facilityFormSwitch"
        >
          <Switch
            checked={showOn}
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled={userCreationLocked}
            onChange={handleChange}
          />
        </Form.Item>
      </Col>
      {showOn ? <>{children}</> : null}
    </>
  );
};

export default UserCreationToggle;
